import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalDate } from "@js-joda/core";
import {
  SegmentController,
  SegmentOption,
} from "components/designV2/segmentcontroller/segmentcontroller";
import { isSettingEnabled } from "model/settings.logic";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { AGIOverview } from "./agiOverview.component";
import { GenerateSalaryNoticesModal } from "./generateSalaryNoticesModal.component";
import { IndividualSalary } from "./individualSalary.component";
import { MonthlySalary } from "./montlySalary/monthlySalary.component";
import { MonthOverview } from "./montlySalary/monthOverview.component";
import { SalaryExportsOverview } from "./salaryExports.component";
import { SalaryTabsHeader } from "./salary.styled";
import { useGenerateSalaryNoticesModal } from "./useGenerateSalaryNoticesModal";
import { isFreeSubscription } from "web-apps/company/utils/subscription.logic";

type SalaryTabKey =
  | "MonthOverview"
  | "Monthly"
  | "Individual"
  | "AGI"
  | "Exports";

export const SalaryTabs: React.FC = () => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;
  const isFree = isFreeSubscription(companyState.subscription.info);
  const [currentTab, setCurrentTab] = useState<SalaryTabKey>(() =>
    isFree ? "AGI" : "MonthOverview",
  );
  const [monthlySalaryDate, setMonthlySalaryDate] = useState(LocalDate.now());
  const [monthlySalaryRefreshKey, setMonthlySalaryRefreshKey] = useState(0);
  const generateSalaryNoticesModal = useGenerateSalaryNoticesModal({
    companyId,
    companySettings: companyState.companySettings,
    currentDate: monthlySalaryDate,
    onCreated: () => setMonthlySalaryRefreshKey((refreshKey) => refreshKey + 1),
  });

  const haveSalaryModule = useMemo(() => {
    return isSettingEnabled(
      companyState.companySettings,
      "EnableCompanySalaryView",
    );
  }, [companyState.companySettings]);

  const options = useMemo<SegmentOption<SalaryTabKey>[]>(() => {
    if (isFree) {
      const disabledReason = t("Subscription.UpgradeToAccess");
      return [
        {
          value: "MonthOverview",
          label: t("Salary.Salaries"),
          disabledReason,
        },
        {
          value: "Monthly",
          label: t("Salary.MonthlySalary"),
          disabledReason,
        },
        {
          value: "Individual",
          label: t("Salary.IndividualSalary"),
          disabledReason,
        },
        { value: "AGI", label: t("Salary.AGI") },
      ];
    }

    const base: SegmentOption<SalaryTabKey>[] = [
      { value: "MonthOverview", label: t("Salary.Salaries") },
    ];
    if (haveSalaryModule) {
      base.push(
        { value: "Monthly", label: t("Salary.MonthlySalary") },
        { value: "Individual", label: t("Salary.IndividualSalary") },
        { value: "AGI", label: t("Salary.AGI") },
      );
    } else {
      base.push({ value: "Exports", label: t("Salary.SalaryExports") });
    }
    return base;
  }, [haveSalaryModule, isFree, t]);

  useEffect(() => {
    if (isFree) setCurrentTab("AGI");
  }, [isFree]);

  return (
    <>
      <SalaryTabsHeader>
        <SegmentController
          options={options}
          value={currentTab}
          onChange={setCurrentTab}
        />
      </SalaryTabsHeader>
      {!isFree && currentTab === "MonthOverview" && <MonthOverview />}
      {!isFree && currentTab === "Monthly" && (
        <MonthlySalary
          currentDate={monthlySalaryDate}
          onOpenGenerateSalaryNoticesModal={
            generateSalaryNoticesModal.openModal
          }
          refreshKey={monthlySalaryRefreshKey}
          setCurrentDate={setMonthlySalaryDate}
        />
      )}
      {!isFree && currentTab === "Individual" && <IndividualSalary />}
      {currentTab === "AGI" && <AGIOverview />}
      {!isFree && currentTab === "Exports" && <SalaryExportsOverview />}
      {!isFree && (
        <GenerateSalaryNoticesModal
          avatarUrls={generateSalaryNoticesModal.avatarUrls}
          canCreateSalaryNotices={
            generateSalaryNoticesModal.canCreateSalaryNotices
          }
          cutOffDate={generateSalaryNoticesModal.cutOffDate}
          generateSalaryNoticesMode={
            generateSalaryNoticesModal.generateSalaryNoticesMode
          }
          isGeneratingSalaryNotices={
            generateSalaryNoticesModal.isGeneratingSalaryNotices
          }
          isCutOffWarningOpen={generateSalaryNoticesModal.isCutOffWarningOpen}
          isOpen={generateSalaryNoticesModal.isOpen}
          modalState={generateSalaryNoticesModal.modalState}
          salaryNoticeWorkers={generateSalaryNoticesModal.salaryNoticeWorkers}
          selectedWorkerIds={generateSalaryNoticesModal.selectedWorkerIds}
          onClose={generateSalaryNoticesModal.closeModal}
          onCancelCutOffWarning={generateSalaryNoticesModal.cancelCutOffWarning}
          onCreate={generateSalaryNoticesModal.createSalaryNotices}
          onConfirmCreate={
            generateSalaryNoticesModal.confirmCreateSalaryNotices
          }
          onModeChange={
            generateSalaryNoticesModal.handleGenerateSalaryNoticesModeChange
          }
          onToggleWorker={generateSalaryNoticesModal.toggleSelectedWorker}
          onWorkerActionClick={
            generateSalaryNoticesModal.handleWorkerActionClick
          }
        />
      )}
    </>
  );
};
